import React, { useEffect, useRef, useState } from 'react';
import { sendChat, Thread, ThreadMessage, ChatScope, ChatRef } from '../api/strategy';
import { VoiceScope, VoiceContext, uploadRecording } from '../api/voice';
import { renderRich } from '../richText';
import './SectionChat.css';

interface Props {
  runId: string;
  scope: ChatScope;
  refObj: ChatRef;
  initial?: Thread;
  // When provided, the mic transcribes a spoken message straight into the input
  // (and stores the note at this scope so it also feeds selection/context).
  voice?: { scope: VoiceScope; context: VoiceContext };
  onUpdated?: (t: Thread) => void;
}

const SUGGESTIONS: Record<ChatScope, string[]> = {
  day: ['How aggressive is today overall?', 'Which is the strongest bet, and why?'],
  race: ['Why no bet in this race?', 'What would change your pick here?'],
  bet: ['Why this one over the favourite?', "What's the main risk on this bet?"],
};

// Best MediaRecorder container the browser supports.
const pickMimeType = (): string => {
  const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];
  const MR = (window as any).MediaRecorder;
  if (MR && typeof MR.isTypeSupported === 'function') {
    for (const t of candidates) if (MR.isTypeSupported(t)) return t;
  }
  return '';
};

const SectionChat: React.FC<Props> = ({ runId, scope, refObj, initial, voice, onUpdated }) => {
  const [messages, setMessages] = useState<ThreadMessage[]>(initial?.messages || []);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Mic → transcribe → input ──
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(
    () => () => {
      // Cleanup any live mic stream on unmount.
      streamRef.current?.getTracks().forEach((t) => t.stop());
    },
    []
  );

  const send = async (text: string) => {
    const msg = text.trim();
    if (!msg || sending) return;
    setSending(true);
    setError(null);
    setMessages((prev) => [...prev, { role: 'user', text: msg, at: new Date().toISOString() }]);
    setInput('');
    try {
      const thread = await sendChat(runId, scope, refObj, msg);
      setMessages(thread.messages);
      onUpdated?.(thread);
    } catch (e: any) {
      setError(e?.message || 'Message failed');
      setMessages((prev) =>
        prev.filter((m) => !(m.role === 'user' && m.text === msg && prev.indexOf(m) === prev.length - 1))
      );
    } finally {
      setSending(false);
    }
  };

  const onMicStop = async () => {
    const blob = new Blob(chunksRef.current, { type: recorderRef.current?.mimeType || 'audio/webm' });
    chunksRef.current = [];
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (!blob.size || !voice) return;
    setTranscribing(true);
    setError(null);
    try {
      const saved = await uploadRecording(blob, voice.scope, voice.context);
      if (saved.transcript) {
        setInput((prev) => (prev ? `${prev.trim()} ` : '') + saved.transcript);
      } else if (saved.status === 'awaiting_key') {
        setError('Transcription needs the OpenAI key in racing-api/.env.');
      } else {
        setError('No speech detected — try again.');
      }
    } catch (e: any) {
      setError(e?.message || 'Transcription failed');
    } finally {
      setTranscribing(false);
    }
  };

  const startMic = async () => {
    setError(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setError(
          window.isSecureContext
            ? 'Microphone not supported in this browser.'
            : 'Microphone needs a secure connection (HTTPS or localhost).'
        );
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mime = pickMimeType();
      const rec = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = onMicStop;
      recorderRef.current = rec;
      rec.start();
      setRecording(true);
    } catch (e: any) {
      setError(e?.name === 'NotAllowedError' ? 'Microphone permission denied.' : 'Could not access microphone.');
    }
  };

  const stopMic = () => {
    setRecording(false);
    try {
      recorderRef.current?.stop();
    } catch {
      /* ignore */
    }
  };

  const toggleMic = () => (recording ? stopMic() : startMic());

  const placeholder = recording
    ? 'Listening… tap ⏹ to transcribe'
    : transcribing
    ? 'Transcribing…'
    : 'Message Claude…';

  return (
    <div className="section-chat">
      <div className="sc-messages">
        {messages.length === 0 ? (
          <p className="sc-empty">
            Ask Claude about this {scope}{voice ? ' — type or tap 🎙 to speak' : ''}. What you discuss here is
            weighed in the post-racing review.
          </p>
        ) : (
          messages.map((m, i) => (
            <div key={i} className={`sc-msg ${m.role}`}>
              <span>{renderRich(m.text)}</span>
            </div>
          ))
        )}
        {sending && (
          <div className="sc-msg assistant pending">
            <span className="sc-typing">
              <i /> <i /> <i />
            </span>
          </div>
        )}
      </div>

      {messages.length === 0 && (
        <div className="sc-suggest">
          {SUGGESTIONS[scope].map((s) => (
            <button key={s} type="button" onClick={() => send(s)} disabled={sending}>
              {s}
            </button>
          ))}
        </div>
      )}

      {error && <div className="sc-error">{error}</div>}

      <form
        className="sc-input"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        {voice && (
          <button
            type="button"
            className={`sc-mic${recording ? ' rec' : ''}`}
            onClick={toggleMic}
            disabled={transcribing || sending}
            title={recording ? 'Stop & transcribe' : 'Speak your message'}
            aria-label={recording ? 'Stop and transcribe' : 'Record a voice message'}
          >
            {recording ? '⏹' : transcribing ? '…' : '🎙'}
          </button>
        )}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={placeholder}
          disabled={sending || transcribing}
        />
        <button type="submit" disabled={sending || transcribing || !input.trim()}>
          Send
        </button>
      </form>
    </div>
  );
};

export default SectionChat;
